import { zodResolver } from "@hookform/resolvers/zod";
import { acceptAccessInput } from "@mola/shared";
import { createFileRoute, Link, redirect, useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import type { z } from "zod";
import { Spinner } from "@/components/spinner";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { authClient } from "@/lib/auth-client";
import { apiUrl, errorMessage } from "@/lib/http";

export const Route = createFileRoute("/welcome")({
  beforeLoad: async () => {
    const { data } = await authClient.getSession();
    if (data) throw redirect({ to: "/agents" });
  },
  component: Welcome,
});

/** The token is in the fragment, so the request that fetched this page never saw it. */
function tokenFromHash(hash: string): string | null {
  const token = new URLSearchParams(hash.replace(/^#/, "")).get("token");
  return token || null;
}

const invalidLink = "Dieser Link ist ungültig, abgelaufen oder wurde bereits verwendet.";
const welcomeFormInput = acceptAccessInput.omit({ token: true });
type WelcomeForm = z.infer<typeof welcomeFormInput>;

interface Invite {
  email: string;
  name: string | null;
}

function Welcome() {
  const navigate = useNavigate();
  // The router's hash, and the browser's if the router has not copied it yet.
  // Same path with a new fragment does not remount this page.
  const routerHash = useLocation({ select: (location) => location.hash });
  const token = tokenFromHash(routerHash || window.location.hash);
  const [invite, setInvite] = useState<Invite | "invalid" | "loading">("loading");
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setInvite("invalid");
      return;
    }
    setInvite("loading");
    let cancelled = false;
    void (async () => {
      const response = await fetch(apiUrl("/api/access/preview"), {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ token }),
      });
      if (cancelled) return;
      if (!response.ok) {
        setInvite("invalid");
        return;
      }
      setInvite((await response.json()) as Invite);
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const needsName = invite !== "loading" && invite !== "invalid" && !invite.name;
  const form = useForm<WelcomeForm>({
    resolver: zodResolver(welcomeFormInput),
    defaultValues: { password: "", name: "" },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    if (!token) return;
    if (needsName && !values.name) {
      form.setError("name", { message: "Pflichtfeld" });
      return;
    }
    setFormError(null);
    const response = await fetch(apiUrl("/api/access/accept"), {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        token,
        password: values.password,
        ...(needsName ? { name: values.name } : {}),
      }),
    });
    if (!response.ok) {
      setFormError(
        await errorMessage(response, "Das hat nicht geklappt. Bitte versuchen Sie es erneut."),
      );
      return;
    }
    await navigate({ to: "/agents" });
  });

  return (
    <div className="flex flex-1 items-center justify-center p-4 pb-[12vh] md:p-8 md:pb-[12vh]">
      <div className="w-full max-w-sm space-y-6">
        <div className="space-y-1.5 text-center">
          <h1 className="font-serif text-3xl tracking-tight">Passwort festlegen</h1>
          {invite === "loading" || invite === "invalid" ? null : (
            <p className="text-muted-foreground text-sm">
              Für <span className="font-medium text-foreground">{invite.email}</span>
            </p>
          )}
        </div>
        {invite === "loading" ? <Spinner /> : null}
        {invite === "invalid" ? (
          <div className="space-y-4 text-center">
            <p role="alert" className="text-sm">
              {invalidLink}
            </p>
            <Link to="/" className="text-sm underline decoration-accent underline-offset-4">
              Zur Startseite
            </Link>
          </div>
        ) : null}
        {invite !== "loading" && invite !== "invalid" ? (
          <form
            onSubmit={onSubmit}
            className="space-y-4 rounded-xl border bg-background p-6"
            noValidate
          >
            {/* Password managers save a new password against the username field in
                the same form; without one they have nothing to file it under. Kept
                in the DOM (sr-only, not display:none) because some managers skip
                hidden inputs. */}
            <input
              type="email"
              name="username"
              autoComplete="username"
              value={invite.email}
              readOnly
              tabIndex={-1}
              aria-hidden="true"
              className="sr-only"
            />
            {needsName ? (
              <Field label="Name" htmlFor="name" error={form.formState.errors.name?.message}>
                <Input id="name" autoComplete="name" className="h-10" {...form.register("name")} />
              </Field>
            ) : null}
            <Field
              label="Passwort"
              htmlFor="password"
              error={form.formState.errors.password?.message}
            >
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                className="h-10"
                {...form.register("password")}
              />
            </Field>
            {formError ? (
              <p role="alert" className="text-destructive text-sm">
                {formError}
              </p>
            ) : null}
            <Button type="submit" className="w-full md:h-10" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? "Wird gespeichert…" : "Passwort festlegen"}
            </Button>
          </form>
        ) : null}
      </div>
    </div>
  );
}
